import { analyticsService } from '../index-business';

export interface PaymentEventData {
  ownerId?: string;
  clientId?: string;
  paymentId?: string;
  attendanceId?: string;
  payment?: any;
  amount?: number;
  currency?: string;
  paymentMethod?: string;
  purpose?: string;
  [key: string]: any;
}

export class PaymentTracker {
  static trackMovePayment(data: { payment: any; success: boolean; error?: string; duration?: number }) {
    const eventData = {
      payment_id: data.payment?.id || data.payment?._id,
      amount: data.payment?.amount,
      currency: data.payment?.currency || 'UAH',
      payment_method: data.payment?.paymentMethod || data.payment?.source,
      payment_type: data.payment?.transactionType || data.payment?.type,
      purpose: data.payment?.purpose,
      sub_purpose: data.payment?.subPurpose,
      from_client_id: data.payment?.fromClientId,
      to_client_id: data.payment?.toClientId || data.payment?.clientId,
      owner_id: data.payment?.ownerId,
      move_reason: data.payment?.moveReason || data.payment?.description,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'movePayment',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('Payment Moved', eventData);
  }

  static trackAddPayment(data: {
    clientId: string;
    payment: any;
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const eventData = {
      client_id: data.clientId,
      amount: data.payment?.amount,
      currency: data.payment?.currency || 'UAH',
      payment_method: data.payment?.paymentMethod || data.payment?.source,
      payment_type: data.payment?.transactionType || data.payment?.type,
      purpose: data.payment?.purpose,
      sub_purpose: data.payment?.subPurpose,
      owner_id: data.payment?.ownerId,
      billing_tag: data.payment?.billingTag,
      has_description: data.payment?.description ? true : false,
      has_confirmation_note: data.payment?.paymentConfirmationNote ? true : false,
      payment_date: data.payment?.date || data.payment?.createdAt,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'addPayment',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('Payment Added', eventData);
  }

  static trackReCreatedPayment(data: {
    ownerId: string;
    paymentId: string;
    paymentData: any;
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const eventData = {
      owner_id: data.ownerId,
      payment_id: data.paymentId,
      amount: data.paymentData?.amount,
      currency: data.paymentData?.currency || 'UAH',
      payment_method: data.paymentData?.paymentMethod || data.paymentData?.source,
      purpose: data.paymentData?.purpose,
      recreation_reason: data.paymentData?.reason,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'reCreatedPayment',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('Payment Recreated', eventData);
  }

  static trackAddClientPayment(data: { payment: any; success: boolean; error?: string; duration?: number }) {
    const eventData = {
      client_id: data.payment?.clientId,
      amount: data.payment?.amount,
      currency: data.payment?.currency || 'UAH',
      payment_method: data.payment?.paymentMethod || data.payment?.source,
      payment_type: data.payment?.transactionType || data.payment?.type,
      purpose: data.payment?.purpose,
      sub_purpose: data.payment?.subPurpose,
      owner_id: data.payment?.ownerId,
      billing_tag: data.payment?.billingTag,
      has_description: data.payment?.description ? true : false,
      payment_date: data.payment?.date || data.payment?.createdAt,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'addClientPayment',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('Client Payment Added', eventData);
  }

  static trackUpdatePaymentPurpose(data: {
    ownerId: string;
    id: string;
    updateData: any;
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const eventData = {
      owner_id: data.ownerId,
      payment_id: data.id,
      new_purpose: data.updateData?.purpose,
      new_sub_purpose: data.updateData?.subPurpose,
      new_amount: data.updateData?.amount,
      new_currency: data.updateData?.currency,
      updated_fields: data.updateData ? Object.keys(data.updateData) : [],
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'updatePaymentPurpose',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('Payment Purpose Updated', eventData);
  }

  static trackUpdatePaymentConfirmationNote(data: {
    ownerId: string;
    id: string;
    confirmationNote: string;
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const eventData = {
      owner_id: data.ownerId,
      payment_id: data.id,
      has_confirmation_note: data.confirmationNote ? true : false,
      confirmation_note_length: data.confirmationNote?.length || 0,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'updatePaymentConfirmationNote',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('Payment Confirmation Note Updated', eventData);
  }

  static trackAddPaymentToAttendance(data: {
    ownerId: string;
    clientId: string;
    attendanceId: string;
    payment: any;
    withCreditPayment: boolean;
    billingTag: string;
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const eventData = {
      owner_id: data.ownerId,
      client_id: data.clientId,
      attendance_id: data.attendanceId,
      amount: data.payment?.amount,
      currency: data.payment?.currency || 'UAH',
      payment_method: data.payment?.paymentMethod || data.payment?.source,
      billing_tag: data.billingTag,
      with_credit_payment: data.withCreditPayment,
      payment_type: data.payment?.transactionType || data.payment?.type,
      purpose: data.payment?.purpose,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'addPaymentToAttendance',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('Payment Added to Attendance', eventData);
  }

  static trackAddMyExpense(data: { payment: any; success: boolean; error?: string; duration?: number }) {
    const eventData = {
      amount: data.payment?.amount,
      currency: data.payment?.currency || 'UAH',
      payment_method: data.payment?.paymentMethod || data.payment?.source,
      payment_type: data.payment?.transactionType || data.payment?.type,
      purpose: data.payment?.purpose,
      sub_purpose: data.payment?.subPurpose,
      owner_id: data.payment?.ownerId,
      expense_category: data.payment?.category,
      has_description: data.payment?.description ? true : false,
      has_receipt: data.payment?.receipt || data.payment?.attachments ? true : false,
      expense_date: data.payment?.date || data.payment?.createdAt,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'addMyExpense',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('Expense Added', eventData);
  }

  static trackCancelPayment(data: {
    ownerId: string;
    id: string;
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const eventData = {
      owner_id: data.ownerId,
      payment_id: data.id,
      action_type: 'cancel_payment',
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'cancel',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('Payment Cancelled', eventData);
  }

  static trackPaymentApiCall(
    endpoint: string,
    data: PaymentEventData & {
      success: boolean;
      error?: string;
      duration?: number;
    }
  ) {
    const eventData = {
      ...data,
      event_type: 'api_call',
      api_endpoint: endpoint,
      timestamp: new Date().toISOString(),
    };

    analyticsService.track(`Payment API: ${endpoint}`, eventData);
  }
}

export const trackPaymentSuccess = (endpoint: string, data: PaymentEventData, duration?: number) => {
  PaymentTracker.trackPaymentApiCall(endpoint, {
    ...data,
    success: true,
    duration,
  });
};

export const trackPaymentError = (endpoint: string, error: string, data: PaymentEventData, duration?: number) => {
  PaymentTracker.trackPaymentApiCall(endpoint, {
    ...data,
    success: false,
    error,
    duration,
  });
};

export default PaymentTracker;
